import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BookOpen, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JoinClassPage = ({ onLogin }) => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [classInfo, setClassInfo] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    fetchClassInfo();
  }, [classId]);

  const fetchClassInfo = async () => {
    try {
      const response = await axios.get(`${API}/classes/${classId}/qrcode`);
      setClassInfo(response.data);
    } catch (error) {
      console.error("Error fetching class:", error);
      toast.error("Classe non trouvée");
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    
    if (!firstName.trim() || !lastName.trim()) {
      toast.error("Veuillez remplir votre nom et prénom");
      return;
    }

    setJoining(true);
    try {
      const response = await axios.post(`${API}/join/${classId}`, null, {
        params: {
          first_name: firstName.trim(),
          last_name: lastName.trim()
        }
      });
      
      toast.success(`Bienvenue ${response.data.first_name} !`);
      onLogin(response.data, "student");
      navigate("/dashboard");
    } catch (error) {
      console.error("Error joining:", error);
      toast.error("Erreur lors de l'inscription");
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <BookOpen className="w-12 h-12 text-[#1A365D] animate-pulse mx-auto" />
          <p className="mt-4 text-[#64748B]">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!classInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <Users className="w-16 h-16 text-[#EF4444] mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-[#1A365D] mb-2">Classe non trouvée</h2>
            <p className="text-[#64748B] mb-4">
              Le lien que vous avez utilisé n'est pas valide ou la classe n'existe plus.
            </p>
            <Button onClick={() => navigate("/")} className="bg-[#1A365D]">
              Retour à l'accueil
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 hero-section"
      data-testid="join-class-page"
    >
      <div className="w-full max-w-md">
        <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 rounded-lg bg-[#1A365D] flex items-center justify-center">
                <BookOpen className="w-10 h-10 text-white" />
              </div>
            </div>
            <CardTitle className="text-3xl text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
              SJH-PhiloAI
            </CardTitle>
            <CardDescription className="text-base text-[#64748B]">
              Rejoindre la classe
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-4">
            {/* Class Info */}
            <div className="bg-[#1A365D] text-white rounded-lg p-4 mb-6 text-center">
              <Users className="w-8 h-8 mx-auto mb-2" />
              <h3 className="text-xl font-semibold">{classInfo.class_name}</h3>
              <p className="text-sm opacity-80">{classInfo.display_name}</p>
            </div>

            {/* Form */}
            <form onSubmit={handleJoin} className="space-y-4">
              <div>
                <Label htmlFor="firstName" className="text-[#0F172A]">Prénom</Label>
                <Input 
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Votre prénom"
                  className="mt-1 border-[#E2E8F0] focus:border-[#1A365D]"
                  data-testid="first-name-input"
                  autoFocus
                />
              </div>
              
              <div>
                <Label htmlFor="lastName" className="text-[#0F172A]">Nom</Label>
                <Input 
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Votre nom"
                  className="mt-1 border-[#E2E8F0] focus:border-[#1A365D]"
                  data-testid="last-name-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-12 bg-[#D4AF37] hover:bg-[#B3932E] text-[#0F172A]"
                disabled={joining}
                data-testid="join-button"
              >
                {joining ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Inscription...
                  </>
                ) : (
                  <>
                    <Users className="w-4 h-4 mr-2" />
                    Rejoindre la classe
                  </>
                )}
              </Button>
            </form>
            
            <p className="text-center text-xs text-[#64748B] mt-6">
              Saint-Jean Hulst • Versailles
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default JoinClassPage;
